import { NgClass, NgIf } from '@angular/common';
import { Component, OnDestroy, signal, OnInit } from '@angular/core';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { HelperService } from '../../../services/helper.service';
import { ALLOWED_FILE_SIZE, ALLOWED_FILE_TYPES } from '../../../common/constants/common.constants';
import { ToastService } from '../../../services/toast.service';
import { AuthService } from '../../../services/auth.service';
import { AuthenticatorModalComponent } from '../../../shared/components/authenticator-modal/authenticator-modal.component';
import { Web2ServiceService } from '../../../services/web2-service.service';
import { Subject, interval } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { UsernameValidatorDirective } from '../../../shared/directives/username-validator.directive';
import { emailValidator } from '../../../shared/directives/email-validator.directive';

@Component({
  selector: 'app-profile',
  imports: [NavBarComponent, NgClass, NgIf, FormsModule, ReactiveFormsModule, AuthenticatorModalComponent, UsernameValidatorDirective],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.css'
})
export class ProfileComponent implements OnDestroy {
  activeMenu: string = 'profile';
  showGasOptions = false;
  selectedGas: string | null = null;
  customGasValue: number | null = null;
  profileImageFile: File | null = null;
  profileImageUrl: string | ArrayBuffer | null = null;
  profileForm: FormGroup = new FormGroup({
    email: new FormControl("", [emailValidator]),
    username: new FormControl("", [Validators.required]),
    profileImagePath: new FormControl(""),
    bio: new FormControl(""),
    timeInForce: new FormControl("FAK")
  });
  userData: any;
  is2FAModalOpen = false;
  isSaving = false;
  isSendingRequest = signal<boolean>(false);
  verificationSent = signal<boolean>(false);
  /** Tracks whether the user is actively editing the email to change it */
  isEditingEmail = false;
  private destroy$ = new Subject<void>();
  private verificationPollingInterval = 5000; // Check every 5 seconds
  private usernameCheck$ = new Subject<string>();
  isUsernameTaken = false;
  isValidatingUsername = false;

  constructor(
    private auth: AuthService,
    private service: ApiService,
    private helper: HelperService,
    private toastr: ToastService,
    private web2: Web2ServiceService
  ) {
    this.userData = this.auth.getUserData();
    this.auth.isConnected$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        this.userData = this.auth.getUserData();
        this.updateProfileForm();
      });
    this.updateProfileForm();
    this.setupUsernameValidation();
  }

  private setupUsernameValidation() {
    this.usernameCheck$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(username => {
        if (username.length < 3 || username === (this.userData?.user_name || '')) {
          this.isUsernameTaken = false;
          return [null];
        }
        this.isValidatingUsername = true;
        return this.service.validateUsername(username);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.isValidatingUsername = false;
        if (res === null) return;
        this.isUsernameTaken = res.data === true;
      },
      error: () => {
        this.isValidatingUsername = false;
      }
    });

    this.profileForm.get('username')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.usernameCheck$.next(value || '');
    });
  }

  /**
   * Angular lifecycle hook for component destruction
   * Cleans up subscriptions and stops polling to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets the active menu section
   * @param menu - The menu identifier to activate (profile, account, trading, notifications, export-private-key)
   */
  setActive(menu: string) {
    this.activeMenu = menu;
  }
  /**
   * Selects a gas fee option for transactions
   * @param option - The gas option to select (low, medium, high, or custom)
   */
  selectGas(option: string) {
    this.selectedGas = option;
  }

  /**
   * Checks if the user's email is fully verified
   * Email is verified only when is_email_verified is true AND there's no pending email
   * @returns true if email is verified, false otherwise
   */
  get isEmailVerifiedState(): boolean {
    // Strictly verified only if flag is true AND no email is pending
    return !!this.userData?.is_email_verified && !this.userData?.pending_email;
  }

  /**
   * Determines if resend/send verification button should be displayed
   * Only shown for WEB3 users when email is not verified and email field has a value
   * @returns true if button should be shown, false otherwise
   */
  get shouldShowResendVerification(): boolean {
    return this.userData?.user_type === 'WEB3'
      && !this.isEmailVerifiedState
      && !!this.profileForm.get('email')?.value;
  }

  /**
   * Determines if verification sent notice should be displayed
   * Shows when verification email was sent for the current email in the form
   * @returns true if notice should be shown, false otherwise
   */
  get shouldShowVerificationSentNotice(): boolean {
    const currentEmail = this.profileForm.get('email')?.value;
    return !!this.userData?.pending_email
      && this.userData.pending_email === currentEmail
      && !this.isEmailVerifiedState;
  }

  /**
   * Determines the label text for the email verification button
   * Returns 'Processing...' when request is being sent
   * Returns 'Resend' if verification already pending for current email
   * Returns 'Send Request' for new email verification
   * @returns The button label text
   */
  get verificationActionLabel(): string {
    const currentEmail = this.profileForm.get('email')?.value;
    const isPendingForCurrentEmail = !!this.userData?.pending_email
      && this.userData.pending_email === currentEmail;

    if (this.isSendingRequest()) {
      return 'Processing...';
    }

    return isPendingForCurrentEmail ? 'Resend' : 'Send Request';
  }

  /**
   * Determines if the save profile button should be enabled
   * Button enabled only when form is valid, has changes, and not currently saving
   * @returns true if save button should be enabled, false otherwise
   */
  get canSaveProfileChanges(): boolean {
    return !this.isSaving && this.hasProfileChanges() && this.isProfileSectionValid() && !this.isUsernameTaken && !this.isValidatingUsername;
  }

  /**
   * Checks if any profile fields have been modified
   * Compares current form values with saved userData
   * @returns true if profile has unsaved changes, false otherwise
   */
  private hasProfileChanges(): boolean {
    if (!this.userData) {
      return false;
    }

    const username = (this.profileForm.get('username')?.value || '').trim();
    const bio = this.profileForm.get('bio')?.value || '';

    return username !== (this.userData.user_name || '')
      || bio !== (this.userData.bio || '')
      || !!this.profileImageFile;
  }

  /**
   * Validates the profile section form fields
   * Currently checks username field validity
   * @returns true if profile section is valid, false otherwise
   */
  private isProfileSectionValid(): boolean {
    const usernameControl = this.profileForm.get('username');
    return !!usernameControl && usernameControl.valid;
  }

  private isWeb3EmailChangeValid(): boolean {
    const loginOption: any = this.auth.getLoginOption();
    const isWeb3User = loginOption?.type?.toLowerCase() !== 'web2' || this.userData?.user_type === 'WEB3';
    const emailControl = this.profileForm.get('email');
    const email = (emailControl?.value || '').trim();
    const hasEmailChanged = email !== (this.userData?.email || '');

    if (!isWeb3User || !hasEmailChanged) {
      return true;
    }

    if (emailControl?.invalid) {
      emailControl.markAsTouched();
      this.toastr.error(this.getEmailValidationMessage());
      return false;
    }

    return true;
  }

  private getEmailValidationMessage(): string {
    const emailControl = this.profileForm.get('email');

    if (emailControl?.errors?.['required']) {
      return 'Email address is required';
    }

    return 'Please enter a valid email address';
  }

  /**
   * Updates the profile form with current user data
   * Populates all form controls with user information
   * Disables email input when email is verified (unless editing)
   * Sets profile image URL from user data
   */
  updateProfileForm(): void {
    // Use the verified email if available, otherwise fall back to pending or current email
    const displayEmail = this.isEmailVerifiedState
      ? (this.userData.email || '')
      : (this.userData.pending_email || this.userData.email || '');

    this.profileForm.patchValue({
      email: displayEmail,
      username: this.userData.user_name,
      bio: this.userData.bio,
      profileImagePath: this.userData.profile_picture,
      timeInForce: this.getSavedTimeInForce()
    });

    // Disable email input only when email is verified AND user hasn't started editing a new email
    if (this.isEmailVerifiedState && !this.isEditingEmail) {
      this.profileForm.controls["email"].disable();
    } else {
      this.profileForm.controls["email"].enable();
    }

    this.profileImageUrl = this.userData.profile_picture ? this.userData.profile_picture : null;
  }

  /**
   * Enables email editing for verified emails
   * Sets editing flag and enables the email input field
   * Resets verification sent state to show pending message
   */
  enableEmailEdit(): void {
    this.isEditingEmail = true;
    this.verificationSent.set(false);
    this.profileForm.controls["email"].enable();
  }

  /**
   * Cancels email editing and reverts to verified email
   * Disables editing flag and reverts email field to original verified email
   * Disables email input and resets verification sent state
   */
  cancelEmailEdit(): void {
    this.isEditingEmail = false;
    this.verificationSent.set(false);
    this.profileForm.patchValue({ email: this.userData.email });
    this.profileForm.controls["email"].disable();
  }

  /**
   * Saves profile changes to the backend
   * Validates form before submission
   * Uploads profile image if selected
   * Updates local user data with response
   * Shows success/error notifications
   */
  async updateProfile(): Promise<void> {
    if (!this.hasProfileChanges()) {
      return;
    }

    if (!this.isProfileSectionValid()) {
      if (this.profileForm.get('username')?.errors?.['required']) {
        this.toastr.error("Username is required");
      } else if (this.profileForm.get('username')?.errors?.['usernameLength']) {
        this.toastr.error("Username must be between 3 and 60 characters");
      } else if (this.profileForm.get('username')?.errors?.['usernamePattern']) {
        this.toastr.error("Username can only contain alphanumeric characters and underscores");
      } else if (this.profileForm.get('username')?.errors?.['noAlphaChar']) {
        this.toastr.error("Username must contain at least one alphabetic character");
      } else {
        this.toastr.error("Please fill all fields correctly");
      }
      return;
    }

    if (this.isUsernameTaken || this.isValidatingUsername) {
      this.toastr.error("Please choose a unique username");
      return;
    }

    if (!this.isWeb3EmailChangeValid()) {
      return;
    }

    this.isSaving = true;

    let loginOption: any = this.auth.getLoginOption();
    let formData: any = this.profileForm.getRawValue();
    if (this.profileImageFile) {
      try {
        let resp: { data: string; } = await this.getProfileImageURI();
        formData.profile_picture = resp.data;
      }
      catch (e) {
        this.toastr.error("Unable to upload profile image.");
        return;
      }
    }
    else
      formData.profile_picture = this.userData.profile_picture;

    let payload: any = {
      user_name: formData.username,
      profile_picture: formData.profile_picture,
      bio: formData.bio
    };

    if (loginOption.type.toLowerCase() !== "web2" && formData.email && formData.email !== this.userData.email) {
      payload["email"] = formData.email;
    }

    this.service
      .updateProfile(payload, { "user_id": this.userData.id })
      .subscribe((result: any) => {
        this.isSaving = false;
        // Merge updated data with existing userData to ensure fields like email aren't lost if backend returns partial data
        const updatedData = { ...this.userData, ...result.data };
        this.auth.setUserData(updatedData);
        this.userData = updatedData;
        this.updateProfileForm();
        this.profileForm.markAsPristine();
        this.toastr.success("Profile updated successfully");
      }, (error: HttpErrorResponse) => {
        this.isSaving = false;
        const apiMessage = error?.error?.message || error?.error?.error_message || error?.error?.detail || error?.error?.error;
        this.toastr.error(apiMessage || "Something went wrong. Please try later");
      });
  }

  /**
   * Sends email verification request to backend
   * Validates email format before sending
   * Only allows WEB3 users to update email
   * Starts polling to check verification status
   * Shows appropriate success/error messages
   */
  sendVerificationRequest(): void {
    if (this.profileForm.get('email')?.invalid) {
      this.profileForm.get('email')?.markAsTouched();
      this.toastr.error(this.getEmailValidationMessage());
      return;
    }

    if (this.userData.user_type == "WEB2") {
      this.toastr.error("WEB2 users are not allowed to update email address");
      return;
    }

    this.isSendingRequest.set(true);
    let email: string = this.profileForm.controls["email"].value;
    this.service
      .updateEmailAddress({ email }, this.userData.id)
      .subscribe((result: any) => {
        this.userData.email = email;
        this.userData.pending_email = email; // Store email as pending to disable button until verification completes
        this.userData.is_email_verified = false; // Reset verification since new email needs re-verification
        this.isEditingEmail = false; // Reset editing state after successful send
        this.auth.setUserData(this.userData);
        this.updateProfileForm();
        this.toastr.success("Verification email sent successfully");
        this.isSendingRequest.set(false);
        this.verificationSent.set(true);
        // Start polling for verification status
        this.startVerificationStatusPolling();
      }, (error: HttpErrorResponse) => {
        const apiMessage = error?.error?.message || error?.error?.error_message || error?.error?.detail || error?.error?.error;
        this.toastr.error(apiMessage || "Something went wrong. Please try later");
        this.isSendingRequest.set(false);
      });
  }

  /**
   * Starts automatic polling to check email verification status
   * Polls every 5 seconds until component is destroyed
   * Allows real-time verification status updates without requiring logout/login
   */
  private startVerificationStatusPolling(): void {
    interval(this.verificationPollingInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkVerificationStatus();
      });
  }

  /**
   * Checks the current email verification status from backend
   * Fetches user profile and updates local state if email has been verified
   * Called periodically by startVerificationStatusPolling
   * Silently handles errors to avoid interrupting user experience
   */
  private checkVerificationStatus(): void {
    // Only check if verification was sent and email is not yet verified
    if (!this.verificationSent() || this.isEmailVerifiedState) {
      return;
    }

    this.service.getUserProfile(this.userData.id).subscribe(
      (result: any) => {
        if (result?.data) {
          const updatedUserData = result.data;
          const currentFormEmail = this.profileForm.get('email')?.value;
          
          // Only trigger success if:
          // 1. The account is marked verified
          // 2. AND the email on the server matches what we are waiting for (or pending_email is cleared)
          const isActuallyVerified = updatedUserData.is_email_verified && 
                                     (updatedUserData.email === currentFormEmail && !updatedUserData.pending_email);

          if (isActuallyVerified) {
            this.userData = updatedUserData;
            this.auth.setUserData(this.userData);
            this.updateProfileForm();
            this.verificationSent.set(false);
            if (this.auth.checkEmailVerifiedLocally({ userId: this.userData.id, email: currentFormEmail })) {
              this.toastr.success("Email verified successfully!");
            }
          }
        }
      },
      (error: any) => {
        // Silently fail - don't show error for background polling
      }
    );
  }

  /**
   * Handles profile image file upload
   * Validates file type and size
   * Converts image to base64 data URL for preview
   * Stores file for later upload with profile update
   * @param event - File input change event
   */
  uploadProfileImage(event: Event): void {
    let input: HTMLInputElement = event?.target as HTMLInputElement;
    if (!input.files || input.files.length == 0)
      return;

    let file: File = input.files[0];
    if (!this.helper.validateFileExtension(file.name, ALLOWED_FILE_TYPES.PROFILE_IMAGE)) {
      this.toastr.error("Invalid file type. Please upload a valid image file");
      input.value = "";
      return;
    }

    if (!this.helper.validateFileSize(file.size, ALLOWED_FILE_SIZE.PROFILE_IMAGE)) {
      this.toastr.error("File size should below " + ALLOWED_FILE_SIZE.PROFILE_IMAGE + " MB.")
      input.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.profileImageUrl = reader.result;
    };
    reader.readAsDataURL(file);

    this.profileImageFile = file;
  }

  /**
   * Uploads profile image to backend and gets image URI
   * Converts image file to FormData and sends to API
   * @returns Promise with image data URI from backend
   */
  getProfileImageURI(): Promise<{ data: string; }> {
    let data: FormData = new FormData();
    if (!this.profileImageFile)
      return Promise.resolve({ data: "" });

    data.append("profile_image", this.profileImageFile);
    return this.service
      .getProfileImageURL(data)
      .toPromise();
  }

  /**
   * Clears profile image data
   * Resets profile image file and preview URL
   */
  clearData(): void {
    this.profileImageFile = null;
    this.profileImageUrl = null;
  }

  /**
   * Handles 2FA toggle switch click event
   * Opens 2FA modal for enabling or disabling two-factor authentication
   * Prevents default toggle behavior until modal is confirmed
   * @param event - Toggle switch click event
   */
  handle2FAToggle(event: Event) {
    event.preventDefault();
    if (!this.userData.enable_two_factor) {
      this.is2FAModalOpen = true;
    } else {
      // Logic to disable 2FA or open a disable modal if needed
      // For now, assuming we might want to just toggle it off or show a confirmation
      // But based on request, we just need to fix the behavior when enabling.
      // If the user wants to disable, they usually go through a flow. 
      // The current UI shows a toggle. If it's on, clicking it might mean disable.
      // Let's assume we open the modal for disable as well or just handle the enable case correctly as requested.
      // The request specifically mentioned "when the toggle is enabled... if modal closed... revert".
      // So we are handling the "turn on" interaction.
      this.is2FAModalOpen = true;
    }
  }

  /**
   * Closes 2FA modal dialog
   * Modal confirmation updates userData which drives toggle state
   * If cancelled, userData remains unchanged and toggle stays OFF
   */
  close2FAModal() {
    this.is2FAModalOpen = false;
    // The toggle state in UI is driven by userData.enable_two_factor.
    // Since we prevented default, the toggle didn't change visually.
    // If the modal was for enabling and it finished, userData would be updated and toggle would turn ON.
    // If cancelled, userData is unchanged, so toggle stays OFF.
  }

  /**
   * Updates user's email alert preference on backend
   * Sends preference to API and updates local state
   * Reverts state on error
   * Shows success/error toast notifications
   * @param newValue - The new email alert preference value
   */
  updateEmailAlertPreference(newValue: boolean): void {
    this.userData.enable_email_alerts = newValue;
    this.isSaving = true;
    this.service.updateEmailAlertPreference(newValue)
      .subscribe(
        (result: any) => {
          this.isSaving = false;
          this.toastr.success("Email alert preference updated successfully");
        },
        (error: HttpErrorResponse) => {
          this.isSaving = false;
          const apiMessage = error?.error?.message || error?.error?.error_message || error?.error?.detail || error?.error?.error;
          this.toastr.error(apiMessage || "Something went wrong. Please try later");
          // Revert the toggle on error
          this.userData.enable_email_alerts = !newValue;
        }
      );
  }

  /**
   * Initiates private key export process for WEB2 users
   * Calls Web2Service to handle private key retrieval and export
   */
  async exportPrivateKey(): Promise<void> {
    await this.web2.getPrivateKey();
  }

  /**
   * Formats wallet address by truncating to first 6 and last 8 characters
   * Displays full address if 14 characters or shorter
   * Format: 0x1234...abcdefgh
   * @param address - The wallet address to format
   * @returns Formatted wallet address with ellipsis, or empty string if no address
   */
  formatWallet(address: string | undefined): string {
    if (!address) return '';
    if (address.length <= 14) return address;

    const start = address.slice(0, 6);
    const end = address.slice(-8);

    return `${start}...${end}`;
  }

  /**
   * Updates the Time-In-Force setting for trading orders
   * Validates that a Time-In-Force value is selected
   * Sends update to backend API
   * Shows success/error notifications
   * Handles loading state during update
   */
  async updateTimeInForce() {
    const time_in_force = this.profileForm.controls["timeInForce"].value;

    if (!time_in_force) {
      this.toastr.error("Please select Time-In-Force");
      return;
    }
    this.isSaving = true;
    try {
      const response = await this.service.patchTimeInForce(time_in_force).toPromise();

      if (response?.status_code === 200) {
        const updatedTimeInForce = response?.data?.time_in_force || response?.data?.timeInForce || time_in_force;
        this.userData = {
          ...this.userData,
          time_in_force: updatedTimeInForce,
          timeInForce: updatedTimeInForce
        };
        this.auth.setUserData(this.userData);
        this.profileForm.patchValue({ timeInForce: updatedTimeInForce });
        this.profileForm.get('timeInForce')?.markAsPristine();
        this.toastr.success("Time-In-Force updated successfully");
      }
    } catch (error) {
      this.toastr.error("Something went wrong");
    } finally {
      this.isSaving = false;
    }
  }

  private getSavedTimeInForce(): string {
    return this.userData?.time_in_force || this.userData?.timeInForce || 'FAK';
  }
}
